import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Navbar, Nav, Container, Button } from 'react-bootstrap';
import IconButton from '@mui/material/IconButton';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Typography from '@mui/material/Typography';
import { useThemeMode } from '../ThemeContext';
import NavDropdown from './NavDropdown';
import LogoSvg from '../assets/logo.svg';
import { APP_URLS } from '../helpers/url_helper';

const PRODUCT_SECTIONS = [
  { label: 'Audits', hash: 'audits' },
  { label: 'Corrective Actions', hash: 'corrective-actions' },
  { label: 'Digital Forms & QR', hash: 'digital-forms' },
  { label: 'Policy & Procedure Builder', hash: 'policy-builder' },
  { label: 'Photo Inspector', hash: 'photo-inspector' },
  { label: 'Document Assistant', hash: 'document-assistant' },
  { label: 'Registers/Assets', hash: 'registers-assets' },
];

const SOLUTIONS_ITEMS = [
  {
    label: 'WHS Compliance Software',
    children: [
      { label: 'WHS Compliance Software', href: '/whs-compliance-software' },
      { label: 'Incident Management', href: '/incident-management-software' },
      { label: 'Audit Software', href: '/whs-compliance-audit-software' },
      { label: 'Contractor Management', href: '/contractor-management' },
      { label: 'Certificate of Recognition (COR)', href: APP_URLS.CERTIFICATE_OF_RECOGNITION },
    ],
  },
  {
    label: 'Safety Management Software',
    children: [
      { label: 'Safety Management Software', href: '/safety-management-software' },
      { label: 'Construction Safety Compliance', href: '/construction-safety-compliance' },
      { label: 'Digital WHS Forms', href: '/digital-whs-forms' },
      { label: 'Plant & Equipment Register', href: '/plant-equipment-register-software' },
    ],
  },
  { label: 'SWMS / PCBU Compliance', href: '/swms-pcbu-compliance' },
  { label: 'AI Compliance Assistant', href: '/ai-compliance-assistant' },
];

/**
 * Shared marketing site header (Home, SEO pages, Learning Hub, etc.).
 * Home-section anchors resolve to /#… when not already on the homepage.
 */
function SiteHeader({ activeNav }) {
  const { mode, toggleTheme } = useThemeMode();
  const { pathname } = useLocation();
  const onHome = pathname === '/' || pathname === '';

  const homeHash = (hash) => (onHome ? `#${hash}` : `/#${hash}`);

  const productItems = PRODUCT_SECTIONS.map(({ label, hash }) => ({
    label,
    href: homeHash(hash),
  }));

  return (
    <Navbar
      expand="lg"
      className={`header-bar py-2 ${mode === 'dark' ? 'bg-dark navbar-dark' : 'bg-white navbar-light'}`}
      style={mode === 'light' ? { backgroundColor: '#fff' } : undefined}
    >
      <Container fluid className="px-3">
        <Navbar.Brand as={Link} to="/" className="header-logo">
          <img
            src={LogoSvg}
            alt="Complimate Logo"
            width={180}
            height={50}
            style={{ height: 50, width: 'auto', verticalAlign: 'middle' }}
          />
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="mainNavbar" />
        <Navbar.Collapse id="mainNavbar">
          <Nav
            className="mx-auto mb-2 mb-lg-0 header-nav"
            style={{ gap: 18, fontSize: 18, fontWeight: 200, display: 'flex', alignItems: 'center' }}
          >
            <NavDropdown label="Product" items={productItems} href={homeHash('product')} />
            <NavDropdown label="Solutions" items={SOLUTIONS_ITEMS} href="#solutions" />
            <Nav.Link href={homeHash('pricing')}>Pricing</Nav.Link>
            <Nav.Link
              as={Link}
              to="/learning-hub"
              className={activeNav === 'learning-hub' ? 'learning-hub-active' : undefined}
            >
              Learning Hub
            </Nav.Link>
            <Nav.Link
              as={Link}
              to={APP_URLS.BLOG}
              className={activeNav === 'blog' ? 'nav-link-active' : undefined}
            >
              Blog
            </Nav.Link>
            <Nav.Link
              as={Link}
              to="/contact-us"
              className={activeNav === 'contact' ? 'nav-link-active' : undefined}
            >
              Contact
            </Nav.Link>
          </Nav>
          <IconButton
            onClick={toggleTheme}
            color="inherit"
            aria-label="Toggle colour theme"
            sx={{
              ml: 1,
              backgroundColor: mode === 'dark' ? '#222' : '#e0f7f7',
              borderRadius: '999px',
              px: 2.5,
              py: 1,
              transition: 'background 0.2s',
              '&:hover': {
                backgroundColor: mode === 'dark' ? '#333' : '#b2f5ea',
              },
              boxShadow:
                mode === 'dark'
                  ? '0 2px 8px rgba(0,0,0,0.15)'
                  : '0 2px 8px rgba(38,195,192,0.10)',
            }}
          >
            <Brightness4Icon />
            <Typography variant="caption" sx={{ ml: 0.5 }}>
              {mode === 'light' ? 'Dark' : 'Light'}
            </Typography>
          </IconButton>
          <Button
            as={Link}
            to={APP_URLS.LOGIN}
            id="log_in"
            variant="outline-primary"
            className="btn-outline-teal ms-lg-3 header-login"
          >
            Log In
          </Button>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
}

export default SiteHeader;
